'use client';

import React, { useEffect, useMemo } from 'react';

import { Body, Heading } from '@depop/web-thrift-ui/Typography';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import { YouMightAlsoLikePlaceholder } from '../YouMightAlsoLike/YouMightAlsoLikePlaceholder';

import { YouMightAlsoLikeFilters } from './YouMightAlsoLikeFilters';
import styles from './styles.module.css';

import { ProductImpressionHandler } from '@/components/ProductImpressionHandler';
import { SimilarItemsProductCard } from '@/components/SimilarItems/SimilarItemsProductCard';
import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { VARIANT_IDENTIFIER } from '@/modules/experiments/config';
import { useExperiments } from '@/modules/experiments/useExperiments';
import { useSizeFilters } from '@/modules/filters/useSizeFilters';
import { ImpressionsBatcherProvider } from '@/modules/impressions/ImpressionsBatcherProvider';
import { ModuleName } from '@/modules/impressions/types';
import { applyYMALProductFilters } from '@/modules/product/helpers/applyYMALProductFilters';
import { extractYMALFilterOptions } from '@/modules/product/helpers/extractYMALFilterOptions';
import { filterSizeTreeByGroup } from '@/modules/product/helpers/filterSizeTreeByGroup';
import { mapYMALSizesToCompositeIds } from '@/modules/product/helpers/mapYMALSizesToCompositeIds';
import { useProductBySlugBasicPDP } from '@/modules/product/hooks/useProductBySlugBasicPDP';
import { useProductBySlugExtendedPDP } from '@/modules/product/hooks/useProductBySlugExtendedPDP';
import { useProductFilters } from '@/modules/product/hooks/useProductFilters';
import { type SimilarProduct } from '@/modules/similar/aliases';
import { useYouMightAlsoLike } from '@/modules/similar/hooks/useYouMightAlsoLike';

type Props = {
  sixProductsPerRow?: boolean;
};

export function YouMightAlsoLikeVariant({ sixProductsPerRow }: Props = {}) {
  const tCommon = useTranslations('common');
  const productT = useTranslations('product');
  const { sendActivityTrackerEvent } = useActivityTracker();
  const { data: product } = useProductBySlugBasicPDP();
  const { data: productExtended } = useProductBySlugExtendedPDP();
  const PRODUCT_LIMIT = 150;
  const FILTERED_PRODUCTS_LIMIT = 90;

  const { products, isLoading } = useYouMightAlsoLike(PRODUCT_LIMIT);
  const productFilters = useProductFilters();
  const [{ web_9191_6_products_per_row_filterable_ymal }, bucketSixPerRow] =
    useExperiments(['web_9191_6_products_per_row_filterable_ymal'], {
      deferred: true,
    });
  const isSixProductsPerRow =
    sixProductsPerRow ??
    web_9191_6_products_per_row_filterable_ymal === VARIANT_IDENTIFIER;

  useEffect(() => {
    if (sixProductsPerRow === undefined) {
      bucketSixPerRow();
    }
  }, [sixProductsPerRow, bucketSixPerRow]);

  // Get size filter tree and build size maps
  const { data: sizeFilterTree } = useSizeFilters();
  const { nameToIds, idToName, filteredSizeTree } = useMemo(() => {
    if (!sizeFilterTree?.data || !Array.isArray(sizeFilterTree.data)) {
      return {
        nameToIds: new Map(),
        idToName: new Map(),
        filteredSizeTree: [],
      };
    }

    // Build maps from FULL size tree (for filtering all products)
    const maps = mapYMALSizesToCompositeIds(sizeFilterTree.data);

    // Filter tree by product group (for displaying filter dropdown)
    const productGroup = productExtended?.attributes?.group;
    const filteredTree = filterSizeTreeByGroup(
      sizeFilterTree.data,
      productGroup
    );

    return {
      nameToIds: maps.nameToIds,
      idToName: maps.idToName,
      filteredSizeTree: filteredTree,
    };
  }, [sizeFilterTree, productExtended]);

  const filteredProducts = applyYMALProductFilters(
    products,
    productFilters.filters,
    idToName
  ).slice(0, FILTERED_PRODUCTS_LIMIT);

  const filterOptions = extractYMALFilterOptions(products, nameToIds);

  function handleProductClick(product: SimilarProduct, itemPosition: number) {
    sendActivityTrackerEvent(ActivityTrackerEventType.MODULE_CLICK_ACTION, {
      moduleName: 'pdp_ymal_segment_filtered',
      itemClicked: product.id.toString(),
      itemPosition,
      schemaVersion: '3.0',
    });
  }

  useEffect(() => {
    if (!isLoading && product && !products.length) {
      sendActivityTrackerEvent(ActivityTrackerEventType.NO_SIMILAR_ITEMS, {
        productId: product.id,
        schemaVersion: '3.0',
      });
    }
  }, [isLoading, product, products.length, sendActivityTrackerEvent]);

  if (isLoading) {
    return <YouMightAlsoLikePlaceholder />;
  }

  if (!product || !products.length) {
    return null;
  }

  return (
    <ImpressionsBatcherProvider
      batchLocationId="pdpFilterableYouMightAlsoLike"
      impressionsType="productImpressions"
      eventType={ActivityTrackerEventType.PRODUCT_IMPRESSIONS}
    >
      {filteredProducts.length === 0 ? (
        <div className={styles.emptyStateContainer}>
          <YouMightAlsoLikeFilters
            filters={productFilters.filters}
            filterOptions={filterOptions}
            onSizeFilterChange={productFilters.setSizeFilter}
            onColourFilterChange={productFilters.setColourFilter}
            onPriceFilterChange={productFilters.setPriceFilter}
            onSaleFilterChange={productFilters.setSaleFilter}
            onClearFilters={productFilters.clearAll}
            hasActiveFilters={productFilters.hasActiveFilters}
            activeFiltersCount={productFilters.activeFiltersCount}
            resultsCount={filteredProducts.length}
            isFilterableYMALExperiment={true}
            sizeIdToNameMap={idToName}
            filteredSizeTree={filteredSizeTree}
          />
          <div className={styles.emptyStateTextContainer}>
            <Heading variant="md">{tCommon('NoResults')}</Heading>
            <Body variant="sm-regular" className={styles.emptyStateBody}>
              {productT('YMALFilters.NoResultsBody')}
            </Body>
          </div>
        </div>
      ) : (
        <div className={styles.segments}>
          <YouMightAlsoLikeFilters
            filters={productFilters.filters}
            filterOptions={filterOptions}
            onSizeFilterChange={productFilters.setSizeFilter}
            onColourFilterChange={productFilters.setColourFilter}
            onPriceFilterChange={productFilters.setPriceFilter}
            onSaleFilterChange={productFilters.setSaleFilter}
            onClearFilters={productFilters.clearAll}
            hasActiveFilters={productFilters.hasActiveFilters}
            activeFiltersCount={productFilters.activeFiltersCount}
            resultsCount={filteredProducts.length}
            isFilterableYMALExperiment={true}
            sizeIdToNameMap={idToName}
            filteredSizeTree={filteredSizeTree}
          />
          <div
            role="list"
            className={clsx(styles.productGrid, {
              [styles.sixColumns]: isSixProductsPerRow,
            })}
          >
            {filteredProducts.map((product, index) => (
              <div role="listitem" key={product.id}>
                <ProductImpressionHandler
                  index={index}
                  isLastItem={index === filteredProducts.length - 1}
                  pageNumber={1}
                  moduleName={ModuleName.YouMightAlsoLike}
                >
                  <SimilarItemsProductCard
                    id={product.id}
                    slug={product.slug}
                    pricing={product.pricing}
                    isBoosted={product.is_boosted}
                    product={product}
                    onClick={(product) => handleProductClick(product, index)}
                  />
                </ProductImpressionHandler>
              </div>
            ))}
          </div>
        </div>
      )}
    </ImpressionsBatcherProvider>
  );
}
